import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ProblemDetail } from '@core/api/contracts';
import { CurrentUserApiClient } from './current-user-api.client';
import { mapCurrentUserProblemDetail } from './current-user-problem-detail.mapper';
import { CurrentUserBootstrapResult } from './current-user.models';
import { CurrentUserStore } from './current-user.store';

@Injectable({ providedIn: 'root' })
export class CurrentUserBootstrapService {
  private readonly msal = inject(MsalService);
  private readonly msalBroadcast = inject(MsalBroadcastService);
  private readonly api = inject(CurrentUserApiClient);
  private readonly store = inject(CurrentUserStore);
  private initialized = false;

  initializeMsalSessionRuntime(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.msalBroadcast.inProgress$
      .pipe(filter((status) => status === InteractionStatus.None))
      .subscribe(() => {
        const account = this.resolveActiveAccount();
        this.resetStoreIfAccountChanged(account);
        if (account && this.store.phase() === 'idle') {
          void this.ensureSession();
        }
      });

    this.msalBroadcast.msalSubject$.subscribe((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        const result = message.payload as AuthenticationResult;
        this.msal.instance.setActiveAccount(result.account);
        this.store.resetForAccount(result.account.homeAccountId);
        void this.ensureSession();
        return;
      }

      if (message.eventType === EventType.LOGOUT_START || message.eventType === EventType.LOGOUT_SUCCESS) {
        this.store.clear();
      }
    });
  }

  ensureSession(): Promise<CurrentUserBootstrapResult> {
    const account = this.resolveActiveAccount();

    if (!account) {
      this.store.markUnauthenticated(null);
      return Promise.resolve({ status: 'unauthenticated', redirectTo: '/auth/login' });
    }

    const sessionAccountKey = account.homeAccountId;
    this.resetStoreIfAccountChanged(account);

    const readyResult = this.resolveReadyResult();
    if (readyResult) {
      return Promise.resolve(readyResult);
    }

    const existingRequest = this.store.getLoadingRequest(sessionAccountKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = firstValueFrom(this.api.getMe())
      .then((me): CurrentUserBootstrapResult => {
        const accepted = this.store.completeWithMe(sessionAccountKey, me);
        if (!accepted) {
          return { status: 'stale' };
        }

        return this.resultFromMe(me);
      })
      .catch((error: unknown) => this.handleBootstrapError(sessionAccountKey, error))
      .finally(() => this.store.clearLoadingRequest(sessionAccountKey));

    this.store.beginLoading(sessionAccountKey, request);
    return request;
  }

  refresh(): Promise<CurrentUserBootstrapResult> {
    const account = this.resolveActiveAccount();
    if (!account) {
      this.store.markUnauthenticated(null);
      return Promise.resolve({ status: 'unauthenticated', redirectTo: '/auth/login' });
    }

    this.store.resetForAccount(account.homeAccountId);
    return this.ensureSession();
  }

  private resolveActiveAccount(): AccountInfo | null {
    const activeAccount = this.msal.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msal.instance.getAllAccounts();
    const fallbackAccount = accounts[0] ?? null;
    if (fallbackAccount) {
      this.msal.instance.setActiveAccount(fallbackAccount);
    }

    return fallbackAccount;
  }

  private resetStoreIfAccountChanged(account: AccountInfo | null): void {
    const nextKey = account?.homeAccountId ?? null;
    if (this.store.sessionAccountKey() !== nextKey) {
      this.store.resetForAccount(nextKey);
    }
  }

  private resolveReadyResult(): CurrentUserBootstrapResult | null {
    const phase = this.store.phase();
    const me = this.store.me();

    if (phase === 'ready' && me) {
      return this.resultFromMe(me);
    }
    if (phase === 'access-denied') {
      return {
        status: 'access-denied',
        deniedReason: this.store.deniedReason() ?? 'generic-access-denied',
        redirectTo: '/auth/access-denied',
      };
    }
    if (phase === 'unauthenticated') {
      return { status: 'unauthenticated', redirectTo: '/auth/login' };
    }

    return null;
  }

  private resultFromMe(me: NonNullable<ReturnType<CurrentUserStore['me']>>): CurrentUserBootstrapResult {
    if (me.appState === 'ONBOARDING_REQUIRED') {
      return {
        status: 'onboarding-required',
        me,
        redirectTo: '/onboarding/developer',
      };
    }

    return {
      status: 'active',
      me,
      redirectTo: this.store.resolveDefaultRedirectUrl(),
    };
  }

  private handleBootstrapError(sessionAccountKey: string, error: unknown): CurrentUserBootstrapResult {
    if (this.store.sessionAccountKey() !== sessionAccountKey) {
      return { status: 'stale' };
    }

    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.store.markUnauthenticated(sessionAccountKey);
      return { status: 'unauthenticated', redirectTo: '/auth/login' };
    }

    if (error instanceof HttpErrorResponse && error.status === 403) {
      const deniedReason = mapCurrentUserProblemDetail(error.error as ProblemDetail | null | undefined);
      this.store.markAccessDenied(sessionAccountKey, deniedReason);
      return {
        status: 'access-denied',
        deniedReason,
        redirectTo: '/auth/access-denied',
      };
    }

    this.store.markError(sessionAccountKey);
    return { status: 'error', redirectTo: '/auth/access-denied' };
  }
}
